import React from "react";
import { MdCloudUpload, MdDelete, MdImage, MdPerson } from "react-icons/md";
import Card from "components/card";
import { BiaFitUser } from "../../types/user";
import LoadingSpinner from "../common/LoadingSpinner";
import { formatNumber } from "../../utils/formatters";
import { formatDateForDisplay } from "../../utils/dateUtils";
import { ImageUpload } from "../../hooks/useImageUpload";

interface UserProfileCardProps {
  user: BiaFitUser;
  fullName: string;
  isMyProfile: boolean;
  image: ImageUpload;
}

const UserProfileCard: React.FC<UserProfileCardProps> = ({
  user,
  fullName,
  isMyProfile,
  image,
}) => {
  return (
    <Card extra="w-full p-3 md:p-6">
      <div className="flex flex-col items-start space-y-6 md:flex-row md:space-x-6 md:space-y-0">
        {/* User Profile Image */}
        <div className="w-full flex-shrink-0 md:w-auto">
          <div className="group relative">
            {user.profile_image?.full_path ? (
              <>
                <img
                  src={user.profile_image.full_path}
                  alt={fullName || user.email}
                  className="h-48 w-full rounded-xl object-cover shadow-lg md:w-48"
                />
                {/* Hover overlay for upload/delete */}
                <div className="bg-black absolute inset-0 flex items-center justify-center space-x-2 rounded-xl bg-opacity-50 opacity-0 transition-opacity group-hover:opacity-100">
                  {/* Upload button */}
                  {isMyProfile && (
                    <button
                      onClick={image.triggerUpload}
                      disabled={image.uploading || image.deleting}
                      className="rounded-full bg-white p-2 transition-colors hover:bg-gray-100 disabled:opacity-50"
                      title="Change image"
                    >
                      <MdCloudUpload className="h-5 w-5 text-gray-700" />
                    </button>
                  )}
                  {/* Delete button */}
                  <button
                    onClick={image.openDeleteModal}
                    disabled={image.uploading || image.deleting}
                    className="rounded-full bg-red-600 p-2 transition-colors hover:bg-red-700 disabled:opacity-50"
                    title="Delete image"
                  >
                    <MdDelete className="h-5 w-5 text-white" />
                  </button>
                </div>
                {image.uploading && (
                  <div className="bg-black absolute inset-0 flex items-center justify-center rounded-xl bg-opacity-50">
                    <LoadingSpinner bare size="lg" tone="white" />
                  </div>
                )}
              </>
            ) : (
              <>
                {/* No image placeholder */}
                {isMyProfile ? (
                  <div
                    onClick={image.triggerUpload}
                    className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-white bg-gradient-to-br from-biafit-primary to-biafit-secondary shadow-lg transition-opacity hover:opacity-90 md:w-48"
                  >
                    {image.uploading ? (
                      <LoadingSpinner bare size="lg" tone="white" />
                    ) : (
                      <>
                        <MdImage className="mb-2 h-12 w-12 text-white" />
                        <span className="text-sm font-medium text-white">
                          Upload Image
                        </span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex h-48 w-full flex-col items-center justify-center rounded-xl bg-gradient-to-br from-biafit-primary to-biafit-secondary shadow-lg md:w-48">
                    <MdPerson className="mb-2 h-20 w-20 text-white" />
                    <p className="text-sm font-medium text-white">
                      No Profile Image
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Hidden file input */}
          {isMyProfile && (
            <input
              ref={image.fileInputRef}
              type="file"
              accept={image.accept}
              onChange={image.handleFileChange}
              className="hidden"
            />
          )}

          {/* Image upload hint */}
          {isMyProfile && !image.uploading && (
            <div className="mt-2 w-full max-w-48 text-center text-xs text-gray-500 dark:text-gray-400">
              Click to upload JPG, JPEG, or PNG (max 20MB)
            </div>
          )}
        </div>

        {/* User Info */}
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
              {fullName || "N/A"}
            </h1>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${
                user.is_active
                  ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                  : "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
              }`}
            >
              {user.is_active ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                Email Address
              </label>
              <p className="text-navy-700 dark:text-white">{user.email}</p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                  First Name
                </label>
                <p className="text-navy-700 dark:text-white">
                  {user.first_name || "Not provided"}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Last Name
                </label>
                <p className="text-navy-700 dark:text-white">
                  {user.last_name || "Not provided"}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Gender
                </label>
                <p className="capitalize text-navy-700 dark:text-white">
                  {user.gender ? user.gender.replace("_", " ") : "Not provided"}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Date of Birth
                </label>
                <p className="text-navy-700 dark:text-white">
                  {formatDateForDisplay(user.date_of_birth)}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Height (cm)
                </label>
                <p className="text-navy-700 dark:text-white">
                  {user.height ? formatNumber(user.height) : "Not provided"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default UserProfileCard;
